/**
 * 緯度経度を小数 6 桁へ四捨五入する（logposeTech/log_web_issues#333）。
 *
 * Why not Math.round(v * 1e6) / 1e6: v * 1e6 は 32.0000065 → 32000006.499999996 のように
 * 誤差を持ち、境界値を切り捨ててしまう。
 * Why not toPrecision(15): 有効桁を削るため、128.0000034999999 を境界へ持ち上げる。
 * Why not toFixed(6): 2 進の内部表現を丸めるため、35.0000005 が 35 になる。
 */

/**
 * 指数表記を通常の小数表記へ展開する。
 *
 * Why: JS は |v| < 1e-6 を "5e-7" のように書く。そのまま正規表現にかけると小数部を
 * 取り出せず、0.0000005 が丸められないまま素通りする。座標は |v| <= 180 なので
 * 負の指数だけ扱えば足りる。
 */
const expandExponential = (text: string): string => {
  const matched = /^(-?)(\d+)(?:\.(\d+))?e-(\d+)$/i.exec(text);
  if (!matched) return text;
  const [, sign, intPart, fracPart = '', expText] = matched;
  const digits = intPart + fracPart;
  const zeros = Number(expText) - intPart.length;
  return `${sign}0.${'0'.repeat(Math.max(zeros, 0))}${digits}`;
};

export const roundCoordinate = (value: number): number => {
  // Why toString(): Number の内部表現は 2 進で誤差を持つ（35.0000005 の実体は
  // 35.000000499999998…）が、toString() は画面に出るのと同じ 10 進表記 "35.0000005" を
  // 返す。ユーザーが入力した表記どおりに四捨五入するため、こちらを基準にする。
  const text = expandExponential(value.toString());
  const matched = /^(-?)(\d+)\.(\d{7,})$/.exec(text);
  if (!matched) return value; // 小数 6 桁以下なら丸める必要が無い
  const [, sign, intPart, fracPart] = matched;
  const keep = fracPart.slice(0, 6);
  // Why not BigInt: tsconfig の target が es6 なので BigInt リテラルは TS2737 になる。
  // スケール後の最大値は 180 * 1e6 = 180,000,000 で MAX_SAFE_INTEGER より十分小さい。
  let scaled = Number(intPart + keep);
  // Why 符号を外して判定: 四捨五入は絶対値で行う。Math.round は半数値を +∞ 方向へ
  // 丸めるので、-35.5000005 が -35.5 になってしまう。
  if (Number(fracPart[6]) >= 5) scaled += 1;
  const digits = String(scaled).padStart(7, '0');
  return Number(`${sign}${digits.slice(0, -6)}.${digits.slice(-6)}`);
};
